// src/lib/normalize.ts
// Centralized API response normalizer for consistent casing
// Backend returns PascalCase (ASP.NET default), frontend expects camelCase

export interface NormalizedProfile {
  id: string;
  personId?: string;
  email?: string;
  stageName?: string;
  firstName?: string;
  lastName?: string;
  onboardStatus?: number;
  sex?: string;
  ethnicity?: string;
  bodyType?: string;
  height?: number;
  weight?: number;
  age?: number;
  bio?: string;
  moreInfo?: string;
  city?: string;
  state?: string;
  stateCode?: string;
  metroCode?: number | string;
  metroName?: string;
  zipCode?: string;
  country?: string;
  profileImageLocation?: string;
  bannerImageLocation?: string;
  isVerified?: boolean;
  isAdmin?: boolean;
  createdAt?: string;
  updatedAt?: string;
  skills?: string[];
  relocate?: boolean;
  twitterUrl?: string;
  snapchatId?: string;
  url?: string;
  dickSize?: number;
  chest?: string;
  eyes?: string;
  hair?: string;
  nudityLevel?: string;
  alterations?: string;
  boobsSize?: string;
  cup?: string;
  headline?: string;
  availabilityStatus?: string;
}

export interface NormalizedLookupOption {
  key: string;
  label: string;
}

export interface NormalizedLookups {
  ethnicities: NormalizedLookupOption[];
  bodyTypes: NormalizedLookupOption[];
  sexes: NormalizedLookupOption[];
  skills: NormalizedLookupOption[];
  states: NormalizedLookupOption[];
  metros: NormalizedLookupOption[];
  eyes: NormalizedLookupOption[];
  hair: NormalizedLookupOption[];
  hairColors: NormalizedLookupOption[];
  eyeColors: NormalizedLookupOption[];
  nudityLevels: NormalizedLookupOption[];
  alterations: NormalizedLookupOption[];
  boobsSizes: NormalizedLookupOption[];
}

/**
 * Normalize profile API response to consistent camelCase keys
 * Handles both wrapped ({ Profile: {...} }) and unwrapped ({ id, ... }) responses
 */
export function normalizeProfile(data: any): NormalizedProfile | null {
  if (!data) return null;

  // Handle wrapped response (most common from backend)
  const profile = data?.Profile ?? data?.profile ?? data;
  if (!profile || typeof profile !== 'object') return null;

  // If no id field exists, this isn't a valid profile
  const id = profile.Id ?? profile.id;
  if (!id) return null;

  return {
    id: String(id),
    personId: profile.PersonId ?? profile.personId ?? undefined,
    email: profile.Email ?? profile.email ?? undefined,
    stageName: profile.StageName ?? profile.stageName ?? undefined,
    firstName: profile.FirstName ?? profile.firstName ?? undefined,
    lastName: profile.LastName ?? profile.lastName ?? undefined,
    onboardStatus: profile.OnboardStatus ?? profile.onboardStatus ?? undefined,
    sex: profile.Sex ?? profile.sex ?? undefined,
    ethnicity: profile.Ethnicity ?? profile.ethnicity ?? undefined,
    bodyType: profile.BodyType ?? profile.bodyType ?? undefined,
    height: profile.Height ?? profile.height ?? undefined,
    weight: profile.Weight ?? profile.weight ?? undefined,
    age: profile.Age ?? profile.age ?? undefined,
    bio: profile.Bio ?? profile.bio ?? undefined,
    moreInfo: profile.MoreInfo ?? profile.moreInfo ?? undefined,
    city: profile.City ?? profile.city ?? undefined,
    state: profile.State ?? profile.state ?? undefined,
    stateCode: profile.StateCode ?? profile.stateCode ?? undefined,
    metroCode: profile.MetroCode ?? profile.metroCode ?? undefined,
    metroName: profile.MetroName ?? profile.metroName ?? undefined,
    zipCode: profile.ZipCode ?? profile.zipCode ?? profile.PostalCode ?? profile.postalCode ?? undefined,
    country: profile.Country ?? profile.country ?? undefined,
    profileImageLocation: profile.ProfileImageLocation ?? profile.profileImageLocation ?? undefined,
    bannerImageLocation: profile.BannerImageLocation ?? profile.bannerImageLocation ?? undefined,
    isVerified: profile.IsVerified ?? profile.isVerified ?? undefined,
    isAdmin: profile.IsAdmin ?? profile.isAdmin ?? undefined,
    createdAt: profile.CreatedAt ?? profile.createdAt ?? undefined,
    updatedAt: profile.UpdatedAt ?? profile.updatedAt ?? undefined,
    skills: profile.Skills ?? profile.skills ?? undefined,
    relocate: profile.Relocate ?? profile.relocate ?? undefined,
    twitterUrl: profile.TwitterUrl ?? profile.twitterUrl ?? undefined,
    snapchatId: profile.SnapchatId ?? profile.snapchatId ?? undefined,
    url: profile.Url ?? profile.url ?? undefined,
    dickSize: profile.DickSize ?? profile.dickSize ?? undefined,
    chest: profile.Chest ?? profile.chest ?? undefined,
    eyes: profile.Eyes ?? profile.eyes ?? undefined,
    hair: profile.Hair ?? profile.hair ?? undefined,
    nudityLevel: profile.NudityLevel ?? profile.nudityLevel ?? undefined,
    alterations: profile.Alterations ?? profile.alterations ?? undefined,
    boobsSize: profile.BoobsSize ?? profile.boobsSize ?? undefined,
    cup: profile.Cup ?? profile.cup ?? undefined,
    headline: profile.Headline ?? profile.headline ?? undefined,
    availabilityStatus: profile.AvailabilityStatus ?? profile.availabilityStatus ?? undefined,
  };
}

/**
 * Normalize lookup values from API (dropdown options)
 */
export function normalizeLookups(data: any): NormalizedLookups {
  const lookups = data?.Lookups ?? data?.lookups ?? data ?? {};

  const normalizeOptions = (arr: any[] | undefined): NormalizedLookupOption[] => {
    if (!Array.isArray(arr)) return [];
    return arr.map(item => ({
      key: String(item.Key ?? item.key ?? item.Id ?? item.id ?? ''),
      label: String(item.Label ?? item.label ?? item.Name ?? item.name ?? item.Description ?? item.description ?? ''),
    })).filter(opt => opt.key && opt.label);
  };

  return {
    ethnicities: normalizeOptions(lookups.Ethnicities ?? lookups.ethnicities),
    bodyTypes: normalizeOptions(lookups.BodyTypes ?? lookups.bodyTypes),
    sexes: normalizeOptions(lookups.Sexes ?? lookups.sexes),
    skills: normalizeOptions(lookups.Skills ?? lookups.skills),
    states: normalizeOptions(lookups.States ?? lookups.states),
    metros: normalizeOptions(lookups.Metros ?? lookups.metros),
    eyes: normalizeOptions(lookups.Eyes ?? lookups.eyes),
    hair: normalizeOptions(lookups.Hair ?? lookups.hair),
    hairColors: normalizeOptions(lookups.HairColors ?? lookups.hairColors),
    eyeColors: normalizeOptions(lookups.EyeColors ?? lookups.eyeColors),
    nudityLevels: normalizeOptions(lookups.NudityLevels ?? lookups.nudityLevels),
    alterations: normalizeOptions(lookups.Alterations ?? lookups.alterations),
    boobsSizes: normalizeOptions(lookups.BoobsSizes ?? lookups.boobsSizes),
  };
}

/**
 * Generic key normalizer - converts first character to lowercase
 * Use sparingly; prefer explicit normalizers above for type safety
 */
export function normalizeKeys<T extends Record<string, unknown>>(obj: Record<string, unknown>): T {
  if (!obj || typeof obj !== 'object') return obj as T;

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key.charAt(0).toLowerCase() + key.slice(1),
      value
    ])
  ) as T;
}
